import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import {
  Delete,
  Add,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  ManagedUserCreate,
  ManagedUserResponse,
} from "../../types/models/users/managedUser";
import {
  createManagedUser,
  deleteManagedUser,
  getAllManagedUsers,
} from "../../services/ManagedUser/service";
import { useAtom } from "jotai";
import {
  accountRootsAtom,
  fetchAccountRootsAtom,
} from "../../state/atoms/accountRootsAtom";
import ConfirmDialog from "../../components/ConfirmDialog";


const ClientAdminsPage: React.FC = () => {
  const [managedUsers, setManagedUsers] = useState<
    (ManagedUserResponse & { account_root_name?: string })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [dob, setDob] = useState<Date | null>(null);
  const [formData, setFormData] = useState<ManagedUserCreate>({
    email: "",
    password: "",
    username: "",
    first_name: "",
    last_name: "",
    phone: "",
    account_root_id: 0,
    date_of_birth: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<{
    userId: number;
    email: string;
  } | null>(null);

  const [accountRoots] = useAtom(accountRootsAtom);
  const [, fetchAccountRoots] = useAtom(fetchAccountRootsAtom);

useEffect(() => {
  fetchAccountRoots();
}, [fetchAccountRoots]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!accountRoots || accountRoots.length === 0) {
        return;
      }

      try {
        setLoading(true);
        const users = await getAllManagedUsers();
        const enriched = users.map((user) => {
          const accountRootName =
            accountRoots.find((root) => root.id === user.account_root_id)
              ?.name || 
            "Unknown";
          return { ...user, account_root_name: accountRootName };
        });
        setManagedUsers(enriched);
      } catch (err) {
        console.error("Error fetching managed users:", err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [accountRoots]);


  const handleOpenCreate = () => {
    setFormData({
      email: "",
      password: "",
      username: "",
      first_name: "",
      last_name: "",
      phone: "",
      account_root_id: 0,
      date_of_birth: "",
    });
    setDob(null);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => setOpenDialog(false);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      await createManagedUser(formData);
      if (accountRoots && accountRoots.length > 0) {
        setLoading(true);
        const users = await getAllManagedUsers();
        const enriched = users.map((user) => {
          const accountRootName =
            accountRoots.find((root) => root.id === user.account_root_id)
              ?.name ||
            "Unknown";
          return { ...user, account_root_name: accountRootName };
        });
        setManagedUsers(enriched);
        setLoading(false);
      }
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving client admin:", err);
      alert((err as Error).message);
    }
  };

  const handleDeleteClick = (admin: { userId: number; email: string }) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async () => {
    if (!adminToDelete) return;
    setDeleteDialogOpen(false);
    try {
      await deleteManagedUser(adminToDelete.userId);
      if (accountRoots && accountRoots.length > 0) {
        setLoading(true);
        const users = await getAllManagedUsers();
        const enriched = users.map((user) => {
          const accountRootName =
            accountRoots.find((root) => root.id === user.account_root_id)
              ?.name ||
            "Unknown";
          return { ...user, account_root_name: accountRootName };
        });
        setManagedUsers(enriched);
        setLoading(false);
      }
    } catch (err) {
      console.error("Error deleting client admin:", err);
      alert((err as Error).message);
    } finally {
      setAdminToDelete(null);
    }
  };


  const groupedByRoot = useMemo(() => {
    return managedUsers.reduce((acc: Record<string, any[]>, user) => {
      const rootName = user.account_root_name || "Unknown";
      if (!acc[rootName]) acc[rootName] = [];
      acc[rootName].push(user);
      return acc;
    }, {});
  }, [managedUsers]);


  return (
    <Box sx={{ color: "#fff", p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h4">Client Admins</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ backgroundColor: "#2ea043", color: "#fff" }}
        >
          New Admin
        </Button>
      </Box>

      {loading ? (
        <CircularProgress sx={{ color: "#fff" }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {Object.entries(groupedByRoot).map(([rootName, usersInThisRoot]) => (
            <Accordion
              key={rootName}
              sx={{
                backgroundColor: "#20232a",
                color: "#fff",
                mb: 2,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#A8B2D1" }} />}>
                <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>
                  {rootName} ({usersInThisRoot.length} admin
                  {usersInThisRoot.length > 1 ? "s" : ""})
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    backgroundColor: "transparent",
                    borderRadius: 0,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#A8B2D1" }}>
                          Full Name
                        </TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Email</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Phone</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Created At</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usersInThisRoot.map((user) => (
                        <TableRow
                          key={user.id}
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                          }}
                        >
                          <TableCell sx={{ color: "#fff" }}>
                            {user.first_name} {user.last_name}
                          </TableCell>
                          <TableCell sx={{ color: "#fff" }}>
                            {user.email}
                          </TableCell>
                          <TableCell sx={{ color: "#fff" }}>
                            {user.phone || "—"}
                          </TableCell>
                          <TableCell sx={{ color: "#fff" }}>
                            {new Date(user.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() =>
                                handleDeleteClick({
                                  userId: user.id,
                                  email: user.email,
                                })
                              }
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}

          {managedUsers.length === 0 && (
            <Typography sx={{ mt: 2, color: "#A8B2D1" }}>
              No client admins available.
            </Typography>
          )}
        </>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "#161B22", color: "#fff" }}>
          Create Client Admin
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#161B22" }}>
          {[
            "username",
            "email",
            "password",
            "first_name",
            "last_name",
            "phone",
          ].map((field) => (
            <TextField
              key={field}
              label={field
                .replace("_", " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
              name={field}
              fullWidth
              margin="normal"
              value={(formData as any)[field]}
              onChange={handleFormChange}
              sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
            />
          ))}

          <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
            <InputLabel sx={{ color: "#A8B2D1" }}>Account Root</InputLabel>
            <Select
              displayEmpty
              name="account_root_id"
              value={formData.account_root_id}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  account_root_id: Number(e.target.value),
                }))
              }
              sx={{
                color: "#fff",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#A8B2D1" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4f6de5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4f6de5",
                },
                ".MuiSvgIcon-root": { color: "#FFFFFF" },
              }}
            >
              <MenuItem value={0} disabled>
                Select Account Root
              </MenuItem>
              {accountRoots.map((root) => (
                <MenuItem key={root.id} value={root.id}>
                  {root.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              value={dob}
              onChange={(newValue) => {
                setDob(newValue);
                setFormData((prev) => ({
                  ...prev,
                  date_of_birth: newValue
                    ? newValue.toISOString().split("T")[0]
                    : "",
                }));
              }}
              slotProps={{
                textField: {
                  id: "dob-input",
                  fullWidth: true,
                  variant: "outlined",
                  size: "small",
                  sx: {
                    "& .MuiInputLabel-asterisk": { display: "none" },
                    "& .MuiInputLabel-root": { color: "#A8B2D1" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#A8B2D1" },
                      "&:hover fieldset": { borderColor: "#4f6de5" },
                      "&.Mui-focused fieldset": { borderColor: "#4f6de5" },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#FFFFFF",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#161B22", pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: "#A8B2D1" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: "#2ea043" }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Client Admin"
        message={
          adminToDelete
            ? `Are you sure you want to delete ${adminToDelete.email}?`
            : "Are you sure you want to delete this client admin?"
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default ClientAdminsPage;
